/**
 * Per Content Type Overrides (chunk3z-fix87).
 *
 * Visual match for the Core 30 reference's Prompt Customization → Per Content
 * Type Overrides block. Four content-type tabs (Service / Category / Location /
 * Supporting), each with the same set of optional override fields grouped into
 * sections (Single-Pass Engine, Guardrails, Research & Planning, Writing,
 * Parallel Steps, Refinement). Every override has a textarea plus an
 * "Append to default" vs "Override default" mode dropdown.
 *
 * FORM-ONLY for now: this holds state in the component and is NOT yet threaded
 * into the generation pipeline. The "Save Prompt Overrides" button is a
 * placeholder so the layout matches the reference; wiring lands with the
 * Content Generation prompt system.
 */
import { useState, useRef, useEffect } from "react";

/** Auto-growing textarea — expands with content instead of scrolling. */
function AutoTextarea({
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    });
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-bg-card border border-border rounded-md px-2.5 py-2 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-accent resize-none overflow-hidden"
    />
  );
}

type Mode = "append" | "override";

interface OverrideField {
  key: string;
  label: string;
  placeholder: string;
}

interface OverrideGroup {
  heading: string;
  // Tailwind color class for the heading (matches reference's colored labels).
  headingClass: string;
  fields: OverrideField[];
}

const CONTENT_TYPES = [
  { id: "service", label: "Service Pages" },
  { id: "category", label: "Category Pages" },
  { id: "location", label: "Location Pages" },
  { id: "supporting", label: "Supporting Content" },
] as const;

const GROUPS: OverrideGroup[] = [
  {
    heading: "Single-Pass Content Engine",
    headingClass: "text-accent",
    fields: [
      {
        key: "single_pass",
        label: "Single-Pass Writer Prompt",
        placeholder: "Custom single-pass instructions for this content type...",
      },
    ],
  },
  {
    heading: "Guardrails",
    headingClass: "text-warning",
    fields: [
      {
        key: "guardrails",
        label: "Custom Guardrails",
        placeholder: "Custom instructions for custom guardrails...",
      },
    ],
  },
  {
    heading: "Research & Planning",
    headingClass: "text-warning",
    fields: [
      {
        key: "pass1",
        label: "Pass 1: Research Synthesis",
        placeholder: "Custom instructions for pass 1: research synthesis...",
      },
      {
        key: "pass2",
        label: "Pass 2: Strategic Outline",
        placeholder: "Custom instructions for pass 2: strategic outline...",
      },
    ],
  },
  {
    heading: "Writing",
    headingClass: "text-warning",
    fields: [
      {
        key: "pass3",
        label: "Pass 3: Section Draft",
        placeholder: "Custom instructions for pass 3: section draft...",
      },
    ],
  },
  {
    heading: "Parallel Steps",
    headingClass: "text-warning",
    fields: [
      {
        key: "faq",
        label: "FAQ Writer",
        placeholder: "Custom instructions for faq writer...",
      },
      {
        key: "meta",
        label: "Meta Title & Description",
        placeholder: "Custom instructions for meta title & description...",
      },
      {
        key: "schema",
        label: "Schema / JSON-LD",
        placeholder: "Custom instructions for schema / json-ld...",
      },
      {
        key: "image",
        label: "Image Generation",
        placeholder: "Custom instructions for image generation...",
      },
      {
        key: "links",
        label: "External Link Insertion",
        placeholder: "Custom instructions for external link insertion...",
      },
    ],
  },
  {
    heading: "Refinement",
    headingClass: "text-warning",
    fields: [
      {
        key: "pass4",
        label: "Pass 4: Burstiness",
        placeholder: "Custom instructions for pass 4: burstiness...",
      },
      {
        key: "pass5",
        label: "Pass 5: Perplexity",
        placeholder: "Custom instructions for pass 5: perplexity...",
      },
      {
        key: "pass6",
        label: "Pass 6: Human Bookends",
        placeholder: "Custom instructions for pass 6: human bookends...",
      },
      {
        key: "pass7",
        label: "Pass 7: Conversion",
        placeholder: "Custom instructions for pass 7: conversion...",
      },
      {
        key: "pass8",
        label: "Pass 8: Final Check",
        placeholder: "Custom instructions for pass 8: final check...",
      },
    ],
  },
];

// State shape: { "service:pass1": { text, mode }, ... }
type OverrideState = Record<string, { text: string; mode: Mode }>;

export function PromptOverrides() {
  const [activeType, setActiveType] = useState<string>("service");
  const [state, setState] = useState<OverrideState>({});

  const cellKey = (field: string) => `${activeType}:${field}`;
  const get = (field: string) =>
    state[cellKey(field)] ?? { text: "", mode: "append" as Mode };

  function setText(field: string, text: string) {
    setState((p) => ({
      ...p,
      [cellKey(field)]: { text, mode: get(field).mode },
    }));
  }
  function setMode(field: string, mode: Mode) {
    setState((p) => ({
      ...p,
      [cellKey(field)]: { text: get(field).text, mode },
    }));
  }

  return (
    <div>
      <p className="text-text font-semibold text-sm mb-2">
        Per Content Type Overrides
      </p>

      {/* Content-type tabs */}
      <div className="flex gap-1 border-b border-border mb-4">
        {CONTENT_TYPES.map((t) => {
          const active = t.id === activeType;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveType(t.id)}
              className={`px-3 py-1.5 text-sm -mb-px border-b-2 transition-colors ${
                active
                  ? "border-accent text-accent font-medium"
                  : "border-transparent text-text-muted hover:text-text"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-5">
        {GROUPS.map((group) => (
          <div key={group.heading}>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${group.headingClass}`}>
              {group.heading}
            </p>
            <div className="space-y-3">
              {group.fields.map((f) => {
                const cell = get(f.key);
                return (
                  <div key={f.key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-text-muted text-xs">
                        {f.label}{" "}
                        <span className="text-text-subtle">(Optional)</span>
                      </label>
                      <select
                        value={cell.mode}
                        onChange={(e) => setMode(f.key, e.target.value as Mode)}
                        className="bg-bg-card border border-border rounded-md px-2 py-1 text-xs text-text-muted focus:outline-none focus:border-accent"
                      >
                        <option value="append">Append to default</option>
                        <option value="override">Override default</option>
                      </select>
                    </div>
                    <AutoTextarea
                      value={cell.text}
                      onChange={(v) => setText(f.key, v)}
                      placeholder={f.placeholder}
                      rows={2}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => {
            /* Form-only: wiring lands with the Content Generation prompt system. */
          }}
          className="w-full bg-accent text-white text-sm font-medium rounded-md px-4 py-2.5 hover:opacity-90"
        >
          💾 Save Prompt Overrides
        </button>
      </div>
    </div>
  );
}

/** Collapsible "How Custom Prompts Work" explainer matching the reference. */
export function HowCustomPromptsWork() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-bg-subtle">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="text-text-muted text-sm">
          ⓘ How Custom Prompts Work
        </span>
        <span className="text-text-subtle text-sm">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 text-xs text-text-subtle space-y-2 border-t border-border-subtle pt-3">
          <p>
            Your content goes through a pipeline: Research → Outline → Article →
            Correction → Humanize. Each step uses a default prompt that you can
            customize.
          </p>
          <div>
            <p className="text-text-muted font-medium mb-1">Where to put things:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>
                <span className="text-text-muted">Word count and section limits</span> → Custom Guardrails. These rules are enforced on every step.
              </li>
              <li>
                <span className="text-text-muted">Writing style and voice</span> → Voice &amp; Tone section. This tells the AI how your business sounds.
              </li>
              <li>
                <span className="text-text-muted">Specific words to use/avoid</span> → Terminology section.
              </li>
              <li>
                <span className="text-text-muted">Anchor text and linking preferences</span> → Specific Requests.
              </li>
              <li>
                <span className="text-text-muted">Language and regional variant</span> → Region / Language (Business Information).
              </li>
            </ul>
          </div>
          <div>
            <p className="text-text-muted font-medium mb-1">Append vs Override:</p>
            <p>
              <span className="text-text-muted">Append:</span> Your instructions
              are added after the default prompt. Use this to add specific
              requirements without changing how the AI writes.
            </p>
            <p>
              <span className="text-text-muted">Override:</span> Your
              instructions completely replace the default prompt. Only use this
              if you know what you're doing — the default prompts contain
              important formatting and quality rules.
            </p>
            <p className="text-warning">Most uses should use Append mode only.</p>
          </div>
        </div>
      )}
    </div>
  );
}
